import classNames from 'classnames'
import css from 'styled-jsx/css'
import Image from 'next/image'
import { useSiteInfo } from 'shared'
import { PrismicImage } from '../../prismic-types-d'

type Props = {
  className?: string
  type: string // radio, checkbox
  label: string
  selected: boolean
  value: string
  onChange?: (checked: boolean) => void
  tagline: string | null
  icon: PrismicImage
}

const defaultProps = {
  type: 'radio',
}

const AAnswer = ({
  className,
  type,
  label,
  selected,
  value,
  onChange,
  tagline,
  icon,
}: Props) => {
  const siteInfo = useSiteInfo()

  const handleClick = () => {
    if (onChange instanceof Function) {
      if (type === 'radio') {
        onChange(true)
      } else {
        onChange(!selected)
      }
    }
  }

  if (siteInfo.isUS || siteInfo.isUK) {
    return (
      <label
        onClick={handleClick}
        className={classNames(
          className,
          'a-answer flex flex-col items-center justify-center w-[calc(50%-10px)] h-[166px] lg:h-[203px] border border-1 border-primary-dark-teal text-lg shadow-[4px_4px_4px_0_rgba(0,0,0,0.25)] py-6 px-2 md:px-5 text-center leading-tight md:w-[260px]',
          {
            'outline -outline-offset-[3px] outline-[3px] outline-primary-dark-teal font-bold':
              selected,
          }
        )}
      >
        {tagline && (
          <span className="mb-2 text-sm uppercase text-primary-dark-teal font-semibold">
            {tagline}
          </span>
        )}
        <span>{label}</span>
        {icon.url && (
          <Image
            className="h-[60px] w-auto"
            src={icon.url}
            alt={icon.alt ?? ''}
            width={icon.dimensions.width}
            height={icon.dimensions.height}
          />
        )}
      </label>
    )
  }

  return (
    <label
      onClick={handleClick}
      className={classNames(
        className,
        'a-answer relative flex items-center w-full py-3 px-4 font-semibold border-2 cursor-pointer rounded transition-colors duration-300 ease-out',
        {
          'a-answer--selected': selected,
        }
      )}
    >
      {type === 'radio' && (
        <svg
          width="36"
          height="36"
          viewBox="0 0 36 36"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="mie-4 question__radioButton"
        >
          <circle
            className="a-answer__decoration"
            cx="18"
            cy="18"
            r="16"
            stroke="currentColor"
            strokeWidth="3"
            strokeDasharray="1 4"
          />
          <circle
            className="a-answer__outer-ring"
            cx="18"
            cy="18"
            r="11"
            stroke="currentColor"
            strokeWidth="2.5"
          />
          <circle
            className="a-answer__inner-ring"
            cx="18"
            cy="18"
            r="7.5"
            fill="currentColor"
          />
        </svg>
      )}
      {type === 'checkbox' && (
        <div className="a-answer__checkbox mie-4 border-2 px-[0.4rem] py-[0.4rem] md:border-primary-teal">
          <svg
            width="16"
            height="13"
            viewBox="0 0 16 13"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#10A8BA"
              className="a-answer__checkbox-icon"
              d="M5.6 8L2.4 4.8L0 7.2L5.6 12.8L16 2.4L13.6 0L5.6 8Z"
            />
          </svg>
        </div>
      )}
      <span className="question__label-text">{label}</span>
      <style jsx>{styles}</style>
    </label>
  )
}

AAnswer.defaultProps = defaultProps

const styles = css`
  .a-answer {
    color: #fff;
    border-color: #fff;
  }

  @media (min-width: 768px) {
    .a-answer {
      color: #57606c;
      border-color: #57606c;
    }
  }

  .a-answer--selected {
    color: #fff;
    background-color: #10a8ba;
    border-color: #10a8ba;
  }

  .a-answer__inner-ring {
    transform: scale(0);
  }

  .a-answer__decoration {
    opacity: 0;
  }

  .a-answer__checkbox-icon {
    fill-opacity: 0;
  }

  .a-answer--selected .a-answer__inner-ring {
    transform-origin: 50% 50%;
    animation: a-answer__scale-in-circle 0.4s ease forwards;
  }

  .a-answer--selected .a-answer__decoration {
    transform-origin: 50% 50%;
    animation: a-answer__decoration-flourish 0.4s ease forwards;
  }

  .a-answer--selected .a-answer__checkbox {
    background-color: #fff;
    border-color: #10a8ba;
  }

  .a-answer--selected .a-answer__checkbox-icon {
    fill-opacity: 1;
  }

  @keyframes a-answer__scale-in-circle {
    0% {
      transform: scale(0);
    }
    100% {
      transform: scale(1);
    }
  }

  @keyframes a-answer__decoration-flourish {
    from {
      transform: scale(0.8) rotate(0rad);
      opacity: 1;
    }
    50% {
      transform: scale(1);
    }
    70% {
      opacity: 1;
    }
    100% {
      transform: scale(1) rotate(1rad);
      opacity: 0;
    }
  }
`

export { AAnswer }
